import React, { useState, useMemo } from 'react'

import { LinkOutlined } from '@ant-design/icons'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface MerchantOption {
  id: string;
  name: string;
}

interface ProductionBomsListService extends TableService {
  export?: (ids: string[]) => Promise<unknown>;
}

interface ProductionBomsListProps {
  role?: 'admin' | 'operator';
  service: ProductionBomsListService; // New prop
  merchants?: MerchantOption[];
  addJob?: (job: unknown) => void;
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

interface ProductionBomRow {
  id: string;
  name?: string;
  merchant_id?: string;
  output_product?: { sku?: string; name?: string } | null;
  output_inventory_type?: { name?: string } | null;
  production_bom_items_count?: number;
  created_at?: string;
  [key: string]: unknown;
}

const ProductionBomsList: React.FC<ProductionBomsListProps> = ({
  role = 'admin',
  service, // New prop
  merchants = [],
  addJob,
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob)

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  const columns = useMemo<unknown[]>(() => {
    const cols: unknown[] = [
      {
        nameKey: `shared.production_boms.list.columns.name`,
        persist: true,
        sortable: true,
        sortFieldName: 'production_boms.name',
        width: '200px',
        wrap: true,
        cell: ({ id, name }: ProductionBomRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/production_boms/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`${urlPrefix}/production_boms/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      }
    ];

    // Add merchant column for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      cols.push({
        nameKey: `shared.production_boms.list.columns.merchant`,
        wrap: true,
        selector: ({ merchant_id }: ProductionBomRow) => {
          return merchants.find((merchant) => merchant.id === merchant_id)?.name
        },
      });
    }

    cols.push(
      {
        nameKey: `shared.production_boms.list.columns.output_product`,
        sortable: true,
        sortFieldName: 'output_products.name',
        wrap: true,
        cell: ({ output_product }: ProductionBomRow) => {
          if (!output_product) return '-';
          return `${output_product.sku} - ${output_product.name}`;
        },
      },
      {
        nameKey: `shared.production_boms.list.columns.output_inventory_type`,
        sortable: true,
        sortFieldName: 'inventory_types.name',
        wrap: true,
        cell: ({ output_inventory_type }: ProductionBomRow) => {
          return output_inventory_type?.name || t('shared.production_boms.list.default_inventory_type');
        },
      },
      {
        nameKey: `shared.production_boms.list.columns.materials_count`,
        sortable: false,
        width: '120px',
        cell: ({ production_bom_items_count }: ProductionBomRow) => {
          return `${production_bom_items_count || 0} ${t('shared.production_boms.list.items_unit')}`;
        },
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'production_boms.created_at',
        wrap: true,
        selector: ({ created_at }: ProductionBomRow) => created_at,
      }
    );

    return cols;
  }, [role, urlPrefix, merchants, t, hideMerchantUI])

  const supportRules = useMemo<SupportRule[]>(() => {
    const rules: SupportRule[] = [
      {
        label: t(`shared.production_boms.list.filters.name`),
        component: MultipleValue,
        inputName: 'production_boms.name',
        defaultPinned: true,
        createable: true,
      },
      {
        label: t(`shared.production_boms.list.filters.output_product_name`),
        component: MultipleValue,
        inputName: 'output_products.name',
        createable: true,
      },
      {
        label: t(`shared.production_boms.list.filters.output_product_sku`),
        component: MultipleValue,
        inputName: 'output_products.sku',
        createable: true,
      }
    ];

    // Add merchant filter for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      rules.unshift({
        label: t(`shared.production_boms.list.filters.merchant`),
        component: MultipleValue,
        inputName: 'production_boms.merchant_id',
        createable: false,
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
      });
    }

    rules.push(
      {
        label: t(`common.created_at`),
        component: FullDayRangePicker,

        defaultPinned: true,
        sortable: false,
        inputName: 'production_boms.created_at',
      }
    );

    return rules;
  }, [role, t, merchants, hideMerchantUI])

  const actions = useMemo<TableAction[]>(() => [
    {
      name: `common.button.export`,
      className: 'btn btn-primary btn-outline mb-0',
      do: (selected: Iterable<string>) => executeBatchOperation({
        task: service && service.export ? () => service.export!(Array.from(selected)) : undefined,
        url: 'production_boms/export.json',
        selected,
        jobName: 'common.button.export'
      } as any)
    } as TableAction,
  ], [executeBatchOperation, service])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'prefix',
        searchFields: [t('shared.production_boms.list.columns.name')],
      }}
      columnsEditable={true}
      tableId={`${role === 'operator' ? 'Operator' : 'Admin'}ProductionBomsTable`}
      defaultQuery={{
        sorting_field: 'production_boms.created_at',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      actions={actions}
      service={service}
      filterHelpExample={t('shared.production_boms.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/production_boms/list#features`}
    />
  )
}

export default ProductionBomsList
